import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { Menu, ChevronDown, LogOut, LayoutDashboard, Shield, Cpu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";

const linkCls = ({ isActive }) =>
  "text-sm tracking-tight px-0 py-2 " +
  (isActive ? "text-slate-900 font-semibold" : "text-slate-600 hover:text-slate-900") +
  " link-underline";

export default function Navbar() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/dashboard") || location.pathname.startsWith("/admin");

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/70">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
          <div className="h-8 w-8 bg-slate-900 text-white grid place-items-center rounded-sm">
            <Cpu className="h-4 w-4" />
          </div>
          <div className="leading-none">
            <div className="font-display font-bold text-slate-900 text-[17px]">InvestGPU</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">EU · Sovereign Infra</div>
          </div>
        </Link>

        {!isDashboard && (
          <nav className="hidden md:flex items-center gap-8" data-testid="navbar-links">
            <NavLink to="/model" className={linkCls} data-testid="nav-model">{t.nav.model}</NavLink>
            <NavLink to="/invest" className={linkCls} data-testid="nav-invest">{t.nav.invest}</NavLink>
            <NavLink to="/simulator" className={linkCls} data-testid="nav-simulator">{t.nav.simulator}</NavLink>
            <NavLink to="/risks" className={linkCls} data-testid="nav-risks">{t.nav.risks}</NavLink>
            <NavLink to="/faq" className={linkCls} data-testid="nav-faq">{t.nav.faq}</NavLink>
          </nav>
        )}

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="text-xs uppercase tracking-[0.2em] text-slate-600 hover:text-slate-900 flex items-center gap-1 font-bold"
                data-testid="lang-switcher"
              >
                {lang.toUpperCase()} <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem onClick={() => setLang("fr")} data-testid="lang-fr">Français</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">English</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-sm" data-testid="user-menu">
                  {user.name?.split(" ")[0] || user.email} <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm w-56">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> {t.nav.dashboard}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="h-4 w-4 mr-2" /> {t.nav.admin}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> {t.nav.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => navigate("/login")} data-testid="nav-login">
                {t.nav.login}
              </Button>
              <Button size="sm" className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/register")} data-testid="nav-signup">
                {t.nav.signup}
              </Button>
            </div>
          )}

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden rounded-sm" data-testid="mobile-menu-trigger">
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <nav className="mt-12 flex flex-col gap-3 text-sm">
                <Link to="/model" data-testid="mnav-model">{t.nav.model}</Link>
                <Link to="/invest" data-testid="mnav-invest">{t.nav.invest}</Link>
                <Link to="/simulator" data-testid="mnav-simulator">{t.nav.simulator}</Link>
                <Link to="/risks" data-testid="mnav-risks">{t.nav.risks}</Link>
                <Link to="/faq" data-testid="mnav-faq">{t.nav.faq}</Link>
                {!user && <>
                  <Link to="/login" data-testid="mnav-login">{t.nav.login}</Link>
                  <Link to="/register" data-testid="mnav-signup">{t.nav.signup}</Link>
                </>}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
